import { Slot } from '@radix-ui/react-slot';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

import { PropsFrom } from '@/utils/typescript/propsFrom';

type ModalManagerState = {
  modals: { modalComponent: FC; data: PropsFrom<FC>; id: string }[];
  openModal: <ModalComponent = FC>(
    modalComponent: ModalComponent,
    data: PropsFrom<ModalComponent>,
    uuid?: string
  ) => void;
  removeModal: (id: string) => void;
  closeAllModals: () => void;
};

export const useModalManagerStore = create<ModalManagerState>((set, get) => ({
  modals: [],
  openModal: (modalComponent, data, uuid) => {
    const { modals } = get();

    set(() => ({
      modals: [
        ...modals,
        {
          modalComponent,
          data,
          id: uuid || uuidv4(),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      ],
    }));
  },
  removeModal: (id) => {
    set(() => {
      const { modals } = get();

      return {
        modals: modals.filter((modal) => modal.id !== id),
      };
    });
  },
  closeAllModals: () => {
    set(() => {
      const { modals } = get();

      return {
        modals: modals.map((modal) => ({
          ...modal,
          forceClose: true,
        })),
      };
    });
  },
}));

type ManagedModalContextProps = {
  MANAGED_MODAL_PROP_ID: string;
  MANAGED_MODAL_PROP_AFTER_LEAVE: () => void;
  MANAGED_MODAL_PROP_FORCE_CLOSE?: boolean;
};

const managedModalContext = createContext<ManagedModalContextProps | null>(
  null
);

const ManagedModalContextProvider = ({
  children,
  MANAGED_MODAL_PROP_AFTER_LEAVE,
  MANAGED_MODAL_PROP_ID,
  MANAGED_MODAL_PROP_FORCE_CLOSE,
}: { children: ReactNode } & ManagedModalContextProps) => {
  const props = useMemo(() => {
    return {
      MANAGED_MODAL_PROP_ID,
      MANAGED_MODAL_PROP_AFTER_LEAVE,
      MANAGED_MODAL_PROP_FORCE_CLOSE,
    };
  }, [
    MANAGED_MODAL_PROP_ID,
    MANAGED_MODAL_PROP_AFTER_LEAVE,
    MANAGED_MODAL_PROP_FORCE_CLOSE,
  ]);

  return (
    <managedModalContext.Provider value={props}>
      {children}
    </managedModalContext.Provider>
  );
};

const useManagedModalContext = () => {
  const context = useContext(managedModalContext);

  if (!context) {
    return null;
  }

  return context;
};

const ModalManager = () => {
  const modals = useModalManagerStore((state) => state.modals);

  return (
    <div>
      {modals.map(({ modalComponent, data, id }) => {
        const ModalComponent = modalComponent as FC;

        return (
          <ModalWrapper id={id} key={id}>
            <ModalComponent {...data} />
          </ModalWrapper>
        );
      })}
    </div>
  );
};

const ModalWrapper = ({
  children,
  id,
  forceClose,
}: {
  children: ReactNode;
  id: string;
  forceClose?: boolean;
}) => {
  const removeModal = useModalManagerStore((state) => state.removeModal);

  const afterLeave = useCallback(() => {
    removeModal(id);
  }, [removeModal, id]);

  return (
    <ManagedModalContextProvider
      MANAGED_MODAL_PROP_ID={id}
      MANAGED_MODAL_PROP_AFTER_LEAVE={afterLeave}
      MANAGED_MODAL_PROP_FORCE_CLOSE={forceClose}
    >
      {children}
    </ManagedModalContextProvider>
  );
};

function ModalTrigger<ModalComponent = FC>({
  onOpen,
  asChild,
  modalComponent,
  children,
}: {
  onOpen: () => PropsFrom<ModalComponent>;
  asChild?: boolean;
  children: React.ReactNode;
  modalComponent: ModalComponent;
}) {
  const Component = asChild ? Slot : 'button';

  const openModal = useOpenModal();

  const id = useId();
  const isOpen = useModalManagerStore((state) =>
    state.modals.some((modal) => modal.id === id)
  );

  return (
    <Component
      id={`modal-trigger-${id}`}
      type="button"
      aria-controls={id}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      onClick={() => {
        const props = onOpen();

        openModal(modalComponent, props, id);
      }}
    >
      {children}
    </Component>
  );
}

const useOpenModal = () => useModalManagerStore((state) => state.openModal);
const useCloseAllModals = () =>
  useModalManagerStore((state) => state.closeAllModals);

export {
  useManagedModalContext,
  ModalManager,
  ModalTrigger,
  useOpenModal,
  useCloseAllModals,
};
